import { EllipsisVertical, Pencil, Trash, X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from '@/components/ui/catalyst/dropdown';
import { Input } from '@/components/ui/catalyst/input';
import { Text } from '@/components/ui/catalyst/text';

interface ThreadTabProps {
  id: string;
  title: string;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onDelete?: (id: string) => void;
  onRename?: (id: string, newTitle: string) => void;
  onRemoveFromTray?: (id: string) => void;
  className?: string;
}

export const ThreadTab = ({
  id,
  title,
  isSelected,
  onSelect,
  onDelete,
  onRename,
  onRemoveFromTray,
  className = '',
}: ThreadTabProps) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState(title);

  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNewTitle(title);
    setIsRenaming(true);
  };

  const handleRename = () => {
    const trimmedTitle = newTitle.trim();
    if (onRename && trimmedTitle !== '' && trimmedTitle !== title) {
      onRename(id, trimmedTitle);
      setIsRenaming(false);
      return;
    }
    setIsRenaming(false);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(id);
    }
  };

  const handleRemoveFromTrayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemoveFromTray) {
      onRemoveFromTray(id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleRename();
    } else if (e.key === 'Escape') {
      setIsRenaming(false);
      setNewTitle(title);
    }
  };

  if (isRenaming) {
    return (
      <div className="bg-white rounded-md shadow-md p-1 flex items-center">
        <Input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          autoFocus
          className="flex-1 text-sm border-gray-200 focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
        />
        <Button
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleRename();
          }}
          className="ml-1"
          color="blue"
        >
          Save
        </Button>
      </div>
    );
  }

  return (
    <Button
      color={isSelected ? 'indigo' : 'zinc'}
      className={`cursor-pointer px-2 py-0.5 flex items-center space-x-1 justify-between! ${className}`}
      onClick={() => onSelect(id)}
    >
      <Text className="truncate text-white!">{title}</Text>
      <Dropdown>
        <DropdownButton
          as="button"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          className="p-1 rounded-full flex items-center justify-center opacity-80 hover:opacity-100"
          aria-label="Thread actions"
        >
          <EllipsisVertical className="h-3.5 w-3.5" />
        </DropdownButton>
        <DropdownMenu className="shadow-lg border border-gray-100 bg-white/95 backdrop-blur-lg z-20">
          {onRename && (
            <DropdownItem
              onClick={handleRenameClick}
              className="gap-2 hover:bg-blue-50"
            >
              <Pencil className="h-4 w-4 text-blue-500" data-slot="icon" />
              Rename
            </DropdownItem>
          )}
          {onDelete && (
            <DropdownItem
              onClick={handleDeleteClick}
              className="gap-2 text-red-600 hover:bg-red-50"
            >
              <Trash className="h-4 w-4 text-red-500" data-slot="icon" />
              Delete
            </DropdownItem>
          )}
          {onRemoveFromTray && (
            <DropdownItem
              onClick={handleRemoveFromTrayClick}
              className="gap-2 text-red-600! hover:bg-red-50! flex! items-center"
            >
              <X className="h-4 w-4 text-red-500!" data-slot="icon" />
              Close
            </DropdownItem>
          )}
        </DropdownMenu>
      </Dropdown>
    </Button>
  );
};
